import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { XCircle, CheckCircle2, Loader2, Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

interface BookingItem {
  id: number;
  status: string;
  start_time: string;
  end_time: string;
  teacher_first_name: string;
  teacher_last_name: string;
}

interface ConfirmCancelAllData {
  token: string;
  bookings: BookingItem[];
}

export default function AppointmentConfirmCancelAll() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [cancelled, setCancelled] = useState(false);
  const [autoCancelTriggered, setAutoCancelTriggered] = useState(false);

  const { data, isLoading, error } = useQuery<ConfirmCancelAllData>(
    ['appointments', 'confirm-cancel-all', token],
    async () => {
      const response = await api.get(`/appointments/public/confirm-cancel-all/${token}`);
      return response.data.data;
    },
    { enabled: !!token, retry: 1 }
  );

  const confirmMutation = useMutation(
    async () => {
      const response = await api.post(`/appointments/public/confirm-cancel-all/${token}`);
      return response.data;
    },
    {
      onSuccess: (res) => {
        setCancelled(true);
        toast.success(res?.message || 'Alle Termine storniert!');
        setTimeout(() => navigate('/appointments/all'), 3000);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Stornierung');
      }
    }
  );

  if (isLoading || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !data || !data.bookings?.length) {
    const errMsg = (error as any)?.response?.data?.error;
    const isExpired = (error as any)?.response?.status === 410 || errMsg?.includes('abgelaufen');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">
            {isExpired ? 'Link abgelaufen' : 'Link ungültig'}
          </h1>
          <p className="text-gray-600 mb-4">
            {errMsg || 'Dieser Link ist ungültig oder wurde bereits verwendet.'}
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  const { bookings } = data;
  const allCancelled = bookings.length > 0 && bookings.every((b) => b.status === 'cancelled');

  useEffect(() => {
    if (!token || !data || !bookings.length) return;
    if (cancelled || allCancelled) return;
    if (confirmMutation.isLoading || autoCancelTriggered) return;

    setAutoCancelTriggered(true);
    confirmMutation.mutate();
  }, [
    token,
    data,
    bookings,
    cancelled,
    allCancelled,
    confirmMutation.isLoading,
    confirmMutation.mutate,
    autoCancelTriggered
  ]);

  if (cancelled || allCancelled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white rounded-lg shadow-lg p-8">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Alle Termine storniert!</h1>
          <p className="text-gray-600 mb-4">
            {bookings.length} Termin(e) wurden erfolgreich storniert. Sie werden zur Übersicht weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold mb-4">Stornierung aller Termine bestätigen</h1>
        <p className="text-gray-600 mb-4">
          Die folgenden {bookings.length} Termin(e) werden storniert und die Slots wieder freigegeben:
        </p>
        <ul className="space-y-3 mb-6">
          {bookings.map((b) => (
            <li key={b.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded">
              <Calendar className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <div>
                <div className="font-semibold">
                  {b.teacher_first_name} {b.teacher_last_name}
                </div>
                <div className="text-sm text-gray-600">
                  {format(parseISO(b.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} –{' '}
                  {format(parseISO(b.end_time), 'HH:mm', { locale: de })}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-6 text-sm text-red-800">
          {confirmMutation.isLoading
            ? 'Stornierung läuft automatisch. Bitte warten...'
            : 'Die Stornierung wird automatisch ausgelöst. Falls nötig, können Sie unten erneut bestätigen.'}
        </div>
        {confirmMutation.isLoading && (
          <div className="w-full px-4 py-2 bg-red-600 text-white rounded flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Wird storniert...
          </div>
        )}
        {confirmMutation.isError && (
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/appointments/all')}
              className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              onClick={() => confirmMutation.mutate()}
              disabled={confirmMutation.isLoading}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <XCircle className="w-5 h-5" />
              Erneut bestätigen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
